import type { ComponentType } from 'svelte';

// Type for our content components - they accept a data prop with brand config
type ContentComponent = ComponentType<{
    data: {
        brand: string;
        config: {
            displayName: string;
            [key: string]: unknown;
        };
    };
}>;

// Dynamic content discovery using import.meta.glob
const defaults = import.meta.glob('/src/content/**/*.md', { eager: true });
const overrides = import.meta.glob('/src/brands/*/content/**/*.md', { eager: true });

// Debug: log what glob patterns found
console.log('[registry] defaults glob results:', Object.keys(defaults));
console.log('[registry] overrides glob results:', Object.keys(overrides));

function keyFromPath(p: string): string {
    return p
        .replace(/^.*?\/content\//, '') // strip up to ".../content/"
        .replace(/\.md$/, '');
}

// Type for markdown module exports
type MarkdownModule = {
    default: ContentComponent;
};

const defaultMap: Record<string, ContentComponent> = {};
for (const [p, mod] of Object.entries(defaults)) {
    defaultMap[keyFromPath(p)] = (mod as MarkdownModule).default;
}

const brandMap = new Map<string, Record<string, ContentComponent>>();
for (const [p, mod] of Object.entries(overrides)) {
    const m = p.match(/\/brands\/([^/]+)\/content\//);
    if (!m) continue;
    const brand = m[1];
    // Map 100xdevelopers to 100xdevelopers.com for brand matching
    const brandKey = brand === '100xdevelopers' ? '100xdevelopers.com' : brand;
    const key = keyFromPath(p);
    const bag = brandMap.get(brandKey) ?? {};
    bag[key] = (mod as MarkdownModule).default;
    brandMap.set(brandKey, bag);
}

export function resolveContent(key: string, brand?: string): ContentComponent | undefined {
    if (brand) {
        const byBrand = brandMap.get(brand);
        if (byBrand?.[key]) {
            return byBrand[key];
        }
    }
    return defaultMap[key];
}
